import {
  ListViewFieldReadable,
  ListViewFilter,
  ListViewReadable,
  ListViewReturnFilterReadable,
  listViewsCreateMutation,
  listViewsFieldsRetrieveOptions,
  listViewsListQueryKey,
  listViewsPartialUpdateMutation,
  listViewsRetrieveOptions,
  listViewsRetrieveQueryKey,
  PaginatedReturnUserListReadable,
  ReturnUserReadable,
  UsersListData,
  usersListInfiniteOptions
} from '@crm/api'
import { DndZone, FilterSet, InfiniteSelect, InputLogic } from '@crm/components'
import { DragContextProps } from '@crm/components/DndComponents/widgets/DragContext'
import { fieldTypeMap } from '@crm/features/leads/utils'
import { defaultNonPagination } from '@crm/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  Radio,
  Row,
  Select,
  Space
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { AxiosError } from 'axios'
import { Add, HambergerMenu, Trash } from 'iconsax-react'
import { useCallback, useEffect, useState } from 'react'

import type { ViewsFieldsProps } from '@crm/types'

const formName = 'list-view'

type Props = { onCancel?: () => void; id?: number | null } & ModalProps

export const AddLeadView = ({ id, ...props }: Props) => {
  const { t } = useLocales()

  const [form] = Form.useForm()
  const queryClient = useQueryClient()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const [fieldsCount, setFieldsCount] = useState(1)
  const [filters, setFilters] = useState<ListViewFilter[]>([
    {
      value: [],
      field_name: '',
      filter_operator: undefined,
      order: 0
    }
  ])

  const [isPrivate, setIsPrivate] = useState(false)
  const [fieldsFilter, setFieldsFilter] = useState(defaultNonPagination)

  const { data: viewsFields } = useQuery(listViewsFieldsRetrieveOptions())

  const { data, refetch } = useQuery({
    ...listViewsRetrieveOptions({ path: { id: Number(id) } }),
    enabled: !!id
  })

  useEffect(() => {
    if (data) {
      const { is_private, ...rest } = data

      setFieldsCount(rest?.fields?.length ?? 0)

      if (rest?.filters?.length) setFilters(rest?.filters)

      form.setFieldsValue({
        ...rest,
        fields:
          rest?.fields && rest?.fields[0]?.order === 1
            ? rest?.fields
            : rest?.fields?.reverse(),
        is_private: is_private === true ? 1 : 0
      })
    }
  }, [data, form])

  useEffect(() => {
    if (!id) {
      form.setFieldsValue({
        is_private: isPrivate ? 1 : 0
      })
    }
  }, [id, isPrivate, form])

  const mutateHandler = {
    onError: (e: AxiosError) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([listViewsListQueryKey()])
      props.onCancel?.()
    }
  }

  const { mutate: createView } = useMutation({
    ...listViewsCreateMutation(),
    ...mutateHandler
  })

  const { mutate: editView } = useMutation({
    ...listViewsPartialUpdateMutation(),
    ...mutateHandler
  })

  const onSubmit = async ({ fields, filters, ...rest }: AnyObject) => {
    const newFields: ListViewFieldReadable[] = fields
      ? Object.entries(fields).map(([key, value]) => {
          const fieldName = Object.values(value as AnyObject)[0] as string
          const findField = viewsFields?.fields.find(
            (field) => field?.name === fieldName
          )

          return {
            label: findField?.label || '',
            name: findField?.name || '',
            field_type: findField?.type || '',
            order: Number.parseInt(key) + 1
          }
        })
      : []

    const newFilters: ListViewReturnFilterReadable[] = filters
      ? filters.map(
          (
            { field_name, filter_operator, value }: AnyObject,
            index: number
          ) => {
            const findFieldType = viewsFields?.fields.find(
              (field) => field?.name === field_name
            )?.type

            const foundValue =
              findFieldType && fieldTypeMap[findFieldType]
                ? fieldTypeMap[findFieldType](value)
                : value

            return {
              field_name,
              filter_operator,
              value: Array.isArray(foundValue) ? foundValue : [foundValue],
              order: index + 1
            }
          }
        )
      : []

    const data: ListViewReadable = {
      ...rest,
      is_private: Number.parseInt(form.getFieldValue('is_private')) === 1,
      fields: newFields,
      filters: newFilters
    }

    if (id) {
      editView({ path: { id: Number(id) }, body: data })
    } else {
      createView({ body: data })
    }

    await refetch()

    await queryClient.refetchQueries({
      queryKey: [listViewsRetrieveQueryKey({ path: { id: Number(id) } })]
    })
  }

  const onChangeFieldsValues = (values: {
    filters: ListViewReturnFilterReadable[]
  }) => {
    const { filters: fieldsFilters, is_private } = form.getFieldsValue()

    if (values.filters) {
      const index = values.filters
        .map((filter, index) => ({ filter, index }))
        .reverse()
        .find(({ filter }) => !!filter)?.index

      const filters = fieldsFilters.map(
        (filter: ListViewReturnFilterReadable, i: number) => {
          if (i !== index) return filter

          return {
            ...filter,
            ...(values.filters[index] ?? {}),
            filter_operator: values.filters[index]?.field_name
              ? undefined
              : filter.filter_operator,
            value:
              values.filters[index]?.filter_operator ||
              values.filters[index]?.field_name
                ? []
                : filter.value
          }
        }
      )

      setFilters(filters)
      form.setFieldsValue({ filters: filters })
    }

    setIsPrivate(Number.parseInt(is_private) === 1)
  }

  const handleSortEnd = (
    action: (state: { id: number }[]) => { id: number }[]
  ) => {
    const fields =
      form.getFieldsValue().fields?.map((value: { id: number }, i: number) => ({
        ...value,
        id: i
      })) || []

    const newArr = action(fields)

    form.setFieldsValue({
      fields: newArr
    })
  }

  const fieldsItem = useCallback(
    ({ i, contextValue }: { i: number; contextValue: DragContextProps }) => (
      <Row>
        <Col span={24}>
          <Space.Compact block style={{ justifyContent: 'start' }}>
            <Button
              icon={<HambergerMenu size={16} />}
              ref={contextValue.setActivatorNodeRef}
              {...contextValue.listeners}
            />

            <Col span={24} style={{ flex: 1 }}>
              <Form.Item
                name={['fields', i, 'name']}
                rules={[
                  { required: true, message: t('views.name_is_required') }
                ]}
              >
                <Select
                  options={viewsFields?.fields?.map((item) => ({
                    value: item?.name,
                    label: item.label
                  }))}
                />
              </Form.Item>
            </Col>

            {fieldsCount > 1 && (
              <Button
                icon={<Trash size={16} />}
                onClick={() =>
                  setFieldsCount((prevState) => {
                    form.setFieldsValue({
                      fields: form
                        .getFieldsValue()
                        .fields.filter(
                          (_: ViewsFieldsProps, y: number) => y !== i
                        )
                    })
                    return prevState - 1
                  })
                }
              />
            )}
          </Space.Compact>
        </Col>
      </Row>
    ),
    [fieldsCount, form, t, viewsFields?.fields]
  )

  return (
    <Modal
      maskClosable={false}
      title={t(id ? 'filter.edit_view' : 'filter.add_view')}
      cancelText={t('buttons.cancel')}
      okText={t('buttons.save')}
      onOk={form.submit}
      width={800}
      {...props}
    >
      <Form
        name={formName}
        form={form}
        layout="vertical"
        validateTrigger="onBlur"
        onValuesChange={onChangeFieldsValues}
        onFinish={onSubmit}
      >
        <Row gutter={16}>
          <Col span={24} sm={12}>
            <Form.Item
              name="name"
              label={t('views.name')}
              rules={[{ required: true, message: t('views.name_is_required') }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={24} sm={12}>
            <Form.Item name="is_private" label={t('views.visibility')}>
              <Radio.Group
                options={[
                  {
                    label: t('document.public'),
                    value: 0
                  },
                  {
                    label: t('document.private'),
                    value: 1
                  }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {isPrivate && (
          <Row>
            <Col span={24}>
              <Form.Item
                name="shared_users"
                label={t('generic.share_with')}
                rules={[{ required: true }]}
              >
                <InfiniteSelect<
                  UsersListData,
                  PaginatedReturnUserListReadable,
                  ReturnUserReadable
                >
                  mode="multiple"
                  getQueryFunction={usersListInfiniteOptions}
                  placeholder={t('main.select')}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row>
          <Col span={24}>
            <Form.Item required label={t('generic.filters')}>
              <FilterSet
                filters={filters}
                setFilters={setFilters}
                dataFields={viewsFields}
                form={form}
                fieldsFilter={fieldsFilter}
                setFieldsFilter={setFieldsFilter}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="filter_logic"
              label={t('views.filter_logic')}
              rules={[
                { required: true, message: t('views.value_is_required') }
              ]}
            >
              <InputLogic
                data={filters?.map((filter) => ({
                  ...filter,
                  title: viewsFields?.fields?.find(
                    (field) => field?.name === filter?.field_name
                  )?.label
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              label={t('generic.fields')}
              required
              style={{ margin: 0 }}
            >
              <DndZone
                items={Array.from({ length: fieldsCount }).map((_, id) => ({
                  id
                }))}
                getSortableItem={({ id }) => id}
                onDragEnd={handleSortEnd}
              >
                {({ item, contextValue }) =>
                  fieldsItem({ i: item.id, contextValue })
                }
              </DndZone>
            </Form.Item>
          </Col>
        </Row>

        <Row justify="start" className="mt-10">
          <Col>
            <Button
              type="text"
              icon={<Add size={16} />}
              onClick={() => setFieldsCount((prevState) => prevState + 1)}
            >
              {t('generic.add_field')}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
